<template>
  <div v-if="passwordPolicy.minimumLength" class="flex items-center gap-2 text-sm">
    <span :class="passwordPolicyChecks.minimumLength ? 'text-green-500' : 'text-red-500'">
      <font-awesome-icon :icon="['fas', passwordPolicyChecks.minimumLength ? 'check' : 'xmark']" />
    </span>
    <span>{{ t('auth.common.password_policy.min_length', { length: passwordPolicy.minimumLength }) }}</span>
  </div>
  <div v-if="passwordPolicy.requireLowercase" class="flex items-center gap-2 text-sm">
    <span :class="passwordPolicyChecks.requireLowercase ? 'text-green-500' : 'text-red-500'">
      <font-awesome-icon :icon="['fas', passwordPolicyChecks.requireLowercase ? 'check' : 'xmark']" />
    </span>
    <span>{{ t('auth.common.password_policy.require_lowercase') }}</span>
  </div>
  <div v-if="passwordPolicy.requireUppercase" class="flex items-center gap-2 text-sm">
    <span :class="passwordPolicyChecks.requireUppercase ? 'text-green-500' : 'text-red-500'">
      <font-awesome-icon :icon="['fas', passwordPolicyChecks.requireUppercase ? 'check' : 'xmark']" />
    </span>
    <span>{{ t('auth.common.password_policy.require_uppercase') }}</span>
  </div>
  <div v-if="passwordPolicy.requireNumbers" class="flex items-center gap-2 text-sm">
    <span :class="passwordPolicyChecks.requireNumbers ? 'text-green-500' : 'text-red-500'">
      <font-awesome-icon :icon="['fas', passwordPolicyChecks.requireNumbers ? 'check' : 'xmark']" />
    </span>
    <span>{{ t('auth.common.password_policy.require_number') }}</span>
  </div>
  <div v-if="passwordPolicy.requireSymbols" class="flex items-center gap-2 text-sm">
    <span :class="passwordPolicyChecks.requireSymbols ? 'text-green-500' : 'text-red-500'">
      <font-awesome-icon :icon="['fas', passwordPolicyChecks.requireSymbols ? 'check' : 'xmark']" />
    </span>
    <span>{{ t('auth.common.password_policy.require_special') }}</span>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  passwordPolicy: {
    minimumLength?: number;
    requireLowercase?: boolean;
    requireUppercase?: boolean;
    requireNumbers?: boolean;
    requireSymbols?: boolean;
  };
  password: string;
}>();

const passwordPolicyChecks = computed(() => {
  const password = props.password;
  const policy = props.passwordPolicy;

  if (!policy) return {};

  return {
    minimumLength: policy.minimumLength ? password.length >= policy.minimumLength : null,
    requireLowercase: policy.requireLowercase ? /[a-z]/.test(password) : null,
    requireUppercase: policy.requireUppercase ? /[A-Z]/.test(password) : null,
    requireNumbers: policy.requireNumbers ? /\d/.test(password) : null,
    requireSymbols: policy.requireSymbols
      ? /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]/.test(password)
      : null
  };
});

const allPasswordPolicyChecksPassed = computed(() => {
  const checks = passwordPolicyChecks.value;
  return Object.values(checks).every((check) => check === null || check);
});

defineExpose({
  allPasswordPolicyChecksPassed
});
</script>
